<template>
    <!-- 保留原有表单结构和所有输入项，优化样式和功能 -->
    <div class="u-form-container">
        <el-form
                ref="menuFormRef"
                :model="localFormData"
                class="u-form"
        >
            <!-- 菜单名称（必填） -->
            <el-form-item class="u-form-item">
                <label class="u-form-label required">菜单名称：</label>
                <div class="u-form-content">
                    <el-input
                            v-model="localFormData.menuName"
                            placeholder="请输入菜单名称"
                            @input="syncFormData"
                            class="u-form-input"
                            size="default"
                            maxlength="20"
                    />
                </div>
            </el-form-item>

            <!-- 菜单序号（新增） -->
            <el-form-item class="u-form-item">
                <label class="u-form-label required">菜单序号：</label>
                <div class="u-form-content">
                    <el-input
                            v-model="localFormData.sortNum"
                            placeholder="请输入最大99数字序号（用于菜单排序）"
                            @input="syncFormData"
                            class="u-form-input"
                            size="default"
                            type="number"
                            min="0"
                            max="99"
                    />
                </div>
            </el-form-item>

            <!-- 菜单图标（整合下拉选择+URL输入） -->
            <el-form-item class="u-form-item">
                <label class="u-form-label">菜单图标：</label>
                <div class="u-form-content icon-input-wrapper">
                    <!-- 整合版：下拉选图标 + 输入URL -->
                    <el-select
                            v-model="localFormData.menuIcon"
                            placeholder="选择图标或输入URL（最大500字符）"
                            class="icon-combo-input"
                            size="default"
                            clearable
                            filterable
                            allow-create
                            @change="handleIconComboChange"
                            @input="handleIconComboInput"
                            maxlength="500"
                            show-word-limit
                    >
                        <el-option
                                v-for="(icon, key) in iconList"
                                :key="key"
                                :label="`${icon.label} (${key})`"
                                :value="key"
                        >
                            <el-icon :class="key" style="margin-right: 8px;">
                                <component :is="key" />
                            </el-icon>
                            {{ icon.label }} ({{ key }})
                        </el-option>
                    </el-select>
                </div>
            </el-form-item>

            <!-- 菜单类型（去掉必填*） -->
            <el-form-item class="u-form-item">
                <label class="u-form-label">菜单类型：</label>
                <div class="u-form-content u-form-radio-group">
                    <el-radio-group v-model="localFormData.menuType" @change="syncFormData" size="default">
                        <el-radio label="1">文件夹</el-radio>
                        <el-radio label="2">菜单</el-radio>
                    </el-radio-group>
                </div>
            </el-form-item>

            <!-- 是否显示 -->
            <el-form-item class="u-form-item">
                <label class="u-form-label">是否显示：</label>
                <div class="u-form-content u-form-radio-group">
                    <el-radio-group v-model="localFormData.isShow" @change="syncFormData" size="default">
                        <el-radio label="0">是</el-radio>
                        <el-radio label="1">否</el-radio>
                    </el-radio-group>
                </div>
            </el-form-item>

            <!-- 菜单URL（菜单类型为2时必填） -->
            <el-form-item class="u-form-item" v-if="localFormData.menuType === '2'">
                <label class="u-form-label required">菜单URL：</label>
                <div class="u-form-content">
                    <el-input
                            v-model="localFormData.menuUrl"
                            placeholder="如：/home/test"
                            @input="syncFormData"
                            class="u-form-input"
                            size="default"
                            maxlength="100"
                    />
                </div>
            </el-form-item>
        </el-form>
    </div>
</template>

<script setup>
    // 保留所有原有导入和业务逻辑，优化回显和图标功能
    import { defineProps, defineEmits, ref, watch, defineExpose,getCurrentInstance } from 'vue';
    import { ElForm, ElFormItem, ElInput, ElRadio, ElRadioGroup, ElSelect, ElOption, ElIcon } from 'element-plus';
    const { $IconList } = getCurrentInstance().appContext.config.globalProperties;//获取Vue实例的代理对象（用于访问全局挂载的属性）

    // 纯JS Props定义（保留原有默认值和校验，新增sortNum）
    const props = defineProps({
        formData: {
            type: Object,
            required: true,
            default: () => ({
                menuName: '',
                menuIcon: '',
                menuType: '1',
                menuUrl: '',
                isShow: '0',
                sortNum: '' // 新增：序号
            })
        }
    });

    // 保留原有Emit定义
    const emit = defineEmits(['update:formData']);

    // 保留原有表单Ref和本地数据
    const menuFormRef = ref({});
    const localFormData = ref({ ...props.formData });
    // 图标列表（从全局挂载里取）
    const iconList = ref($IconList);

    // 优化回显逻辑：深度监听props.formData变化
    watch(() => props.formData, (newVal) => {
        if (newVal && JSON.stringify(newVal) !== JSON.stringify(localFormData.value)) {
            localFormData.value = { ...newVal };
        }
    }, { deep: true, immediate: true });

    // 整合版图标组件 - 下拉选择/输入URL 变更处理
    const handleIconComboChange = () => {
        syncFormData();
    };

    // 整合版图标组件 - 输入实时处理（限制字符长度）
    const handleIconComboInput = (val) => {
        if (val && val.length > 500) {
            localFormData.value.menuIcon = val.substring(0, 500);
        }
        syncFormData();
    };

    // 保留原有数据同步逻辑
    const syncFormData = () => {
        emit('update:formData', { ...localFormData.value });
    };

    // 保留原有表单验证逻辑（核心业务不修改，新增序号校验）
    const validateForm = () => {
        // 1. 基础校验：表单数据是否初始化
        if (!localFormData.value) {
            return { valid: false, message: '表单数据未初始化' };
        }
        const { menuName, menuType, menuUrl, sortNum, menuIcon } = localFormData.value;

        // 规则1：菜单名称不能为空 且 不能大于50
        if (!menuName?.trim()) {
            return { valid: false, message: '请输入菜单名称' };
        }
        if (menuName.trim().length > 50) {
            return { valid: false, message: '菜单名称超长' };
        }

        // 规则2：菜单序号不能为空且不能大于99
        if (sortNum === '' || sortNum === null || sortNum === undefined) {
            return { valid: false, message: '请输入菜单序号' };
        }
        const num = Number(sortNum);
        if (isNaN(num) || num < 0 || num > 99) {
            return { valid: false, message: '菜单序号最大99' };
        }

        // 规则3：菜单图标可以为空，有值时不能大于500 且 必须是url 或 iconList定义的值
        if (menuIcon?.trim()) {
            // 长度校验
            if (menuIcon.trim().length > 500) {
                return { valid: false, message: '菜单图标长度不能超过500个字符' };
            }
            // 格式校验：是iconList中的key 或 合法URL
            const iconKeys = Object.keys(iconList.value);
            const isIconKey = iconKeys.includes(menuIcon.trim());
            // const urlReg = /^(https?:\/\/|\/).+/; 以http/https开头 或 以/开头的相对路径，先保留，如果要内置图标可以用到
            const urlReg = /^https?:\/\/.+/;//必须 http/https 开头
            const isUrl = urlReg.test(menuIcon.trim());
            if (!isIconKey && !isUrl) {
                return { valid: false, message: '菜单图标必须是url或指定类型' };
            }
        }

        // 规则4：当menuType === '2'时 菜单url不能为空 且必须是/test这种格式
        if (menuType === '2') {

            if (!menuUrl?.trim()) {
                return { valid: false, message: '请输入菜单URL' };
            }
            // URL格式正则：以/开头，且不能有空格、特殊字符（仅允许字母/数字/下划线/斜杠）
            const urlFormatReg = /^\/[a-zA-Z0-9_/]+$/;
            if (!urlFormatReg.test(menuUrl.trim())) {
                return { valid: false, message: '菜单URL格式错误' };
            }
        }

        // 所有校验通过
        return { valid: true, message: '表单验证通过' };
    };

    // 保留原有暴露方法
    defineExpose({
        validateForm,
        localFormData
    });
</script>

<style scoped>
    /* 仅保留弹窗内表单的独立样式，避免继承外部污染 */
    .u-form-container {
        padding: 10px;
    }
    .u-form-item {
        margin-bottom: 16px;
        display: flex;
        align-items: flex-start;
    }
    .u-form-label {
        width: 100px;
        text-align: right;
        padding-right: 12px;
        font-size: 14px;
        color: #333;
        line-height: 32px;
    }
    .u-form-label.required::before {
        content: '*';
        color: #f56c6c;
        margin-right: 4px;
    }
    .u-form-content {
        flex: 1;
    }
    .u-form-input {
        width: 100%;
    }
    .u-form-radio-group {
        display: flex;
        gap: 16px;
        line-height: 32px;
    }
    .icon-input-wrapper {
        width: 100%;
    }
    .icon-combo-input {
        width: 100%;
    }
</style>